import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../Icon';
import T from '../components/EditableText';
import HoneypotField from '../components/HoneypotField';
import { submitContactRequest } from '../publicApi';
import { FRAGEN, FragebogenFrage, beantwortete } from '../fragebogen';
import { track, M38_EVENTS } from './tracking';

/**
 * Der Betreuungs-Check — das wichtigste Element der Landingpage.
 *
 * Inhaltlich ist es eine Anfrage zur 24-Stunden-Betreuung wie jede andere und
 * landet im selben Posteingang unter `/anfragen`.
 *
 * **Die Fragen kommen aus `fragebogen.ts`** — derselben Datei, aus der sich das
 * Kontaktformular der Kundenwebsite bedient. Nicht nur die Schlüssel, auch die
 * Antworttexte: Stünde hier eine eigene Liste, hieße dieselbe Auskunft einmal
 * „Schnellstmöglich" und einmal „Möglichst kurzfristig", und keine Auswertung
 * im Posteingang fasste beides mehr zusammen. Aus derselben Datei stammt auch
 * `kurz` — die knappe Beschriftung, mit der aus fünf Schaltflächen
 * „Pflegegrad 1…5" die Ziffern 1 bis 5 werden.
 *
 * Anders ist nur die **Darstellung**: drei kurze Schritte statt eines langen
 * Formulars. Das Konzept besteht darauf, weil ein sichtbar langes Formular an
 * dieser Stelle die Anfrage kostet, um die es der ganzen Kampagne geht.
 *
 * Welche der Fragen gezeigt werden, entscheidet diese Seite: Die Frage nach der
 * Personenzahl im Haushalt steht nicht im Kampagnenkonzept und bleibt deshalb
 * hier weg, ohne dass sie auf der Kundenwebsite verschwindet.
 */

/** Eine Frage aus dem gemeinsamen Fragebogen holen. */
const frageMit = (key: string): FragebogenFrage => {
  const gefunden = FRAGEN.find((f) => f.key === key);
  if (!gefunden) throw new Error(`Frage "${key}" fehlt in fragebogen.ts`);
  return gefunden;
};

const FRAGE_FUER_WEN = frageMit('fuer_wen');
const FRAGE_ZEITRAHMEN = frageMit('zeitrahmen');
const FRAGE_PFLEGEGRAD = frageMit('pflegegrad');
const FRAGE_ORT = frageMit('ort');

/** Wie viele Schritte der Fortschrittsbalken kennt. */
const SCHRITTE = 3;

/**
 * Wie lange die Antwort in Schritt 1 markiert stehen bleibt, bevor es von
 * selbst weitergeht. Kurz genug, dass niemand auf „Weiter" wartet, lang genug,
 * dass man sieht, was man angetippt hat.
 */
const AUTO_WEITER_MS = 300;

const BetreuungsCheck: React.FC = () => {
  const navigate = useNavigate();
  const [schritt, setSchritt] = useState(0);
  const [antworten, setAntworten] = useState<Record<string, string>>({});
  const [ort, setOrt] = useState('');
  const [vorname, setVorname] = useState('');
  const [telefon, setTelefon] = useState('');
  const [email, setEmail] = useState('');
  const [notiz, setNotiz] = useState('');
  const [einwilligung, setEinwilligung] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [begonnen, setBegonnen] = useState(false);

  const autoWeiter = useRef<number>();
  const perPfeiltaste = useRef(false);
  const fokusAufUeberschrift = useRef(false);
  const ueberschrift = useRef<HTMLHeadingElement>(null);

  useEffect(() => () => window.clearTimeout(autoWeiter.current), []);

  // Nach einem Wechsel ohne Klick auf „Weiter" verschwindet mit Schritt 1 auch
  // die angetippte Antwort, die den Fokus hatte. Er landete sonst auf <body>:
  // Tastatur und Vorleseprogramm fingen oben auf der Seite wieder an. Die
  // Überschrift des neuen Schritts nimmt ihn auf und sagt zugleich, wo man ist.
  useEffect(() => {
    if (!fokusAufUeberschrift.current) return;
    fokusAufUeberschrift.current = false;
    ueberschrift.current?.focus();
  }, [schritt]);

  const waehle = (key: string, wert: string) => {
    // Der erste Klick ins Formular ist die Kennzahl „Check begonnen": Sie zeigt,
    // wie viele Besucher anfangen — und im Vergleich zum Absenden, wo sie
    // aussteigen. Nur einmal je Besuch melden.
    if (!begonnen) {
      setBegonnen(true);
      track(M38_EVENTS.checkStart);
    }
    setAntworten((prev) => ({ ...prev, [key]: wert }));
    setError(null);
  };

  /**
   * Schritt 1 hat nur eine Frage, und sie ist Pflicht — nach der Antwort gibt
   * es dort nichts mehr zu tun. Deshalb geht es von selbst weiter, auch wenn
   * jemand nach „Zurück" dieselbe Antwort nochmal antippt.
   *
   * Nicht aber beim Blättern mit den Pfeiltasten: Das wählt bei Radios jede
   * Antwort auf dem Weg gleich mit aus, und wer zur zweiten will, landete nach
   * der ersten schon im nächsten Schritt. Dort bestätigen Leertaste oder
   * „Weiter".
   */
  const waehleUndWeiter = (key: string, wert: string) => {
    waehle(key, wert);
    if (perPfeiltaste.current) return;
    window.clearTimeout(autoWeiter.current);
    autoWeiter.current = window.setTimeout(() => {
      fokusAufUeberschrift.current = true;
      setSchritt((s) => (s === 0 ? 1 : s));
    }, AUTO_WEITER_MS);
  };

  const weiter = () => {
    window.clearTimeout(autoWeiter.current);
    if (schritt === 0 && !antworten[FRAGE_FUER_WEN.key]) {
      setError('Bitte wählen Sie eine Antwort aus.');
      return;
    }
    setError(null);
    setSchritt((s) => Math.min(s + 1, SCHRITTE - 1));
  };

  const zurueck = () => {
    window.clearTimeout(autoWeiter.current);
    setError(null);
    setSchritt((s) => Math.max(s - 1, 0));
  };

  /**
   * Der Text der Anfrage.
   *
   * Der Posteingang verlangt eine Nachricht, der Check besteht aber aus
   * Ankreuzfeldern. Statt „(kein Text)" anzuzeigen, wird hier ein lesbarer Satz
   * gebaut — wer die Anfrage öffnet, sieht auf einen Blick, worum es geht, ohne
   * erst die Zusatzangaben aufzuklappen.
   *
   * Die Telefonnummer steht bewusst mit darin. Sie steht zwar auch im eigenen
   * Feld, macht die Nachricht aber eindeutig: Der Server verwirft Einsendungen
   * mit gleichem Text und gleicher E-Mail als Dublette, und ohne
   * E-Mail-Adresse — die hier optional ist — kollidierten sonst zwei Besucher
   * mit denselben Antworten innerhalb von zehn Minuten.
   */
  const nachricht = (): string => {
    const teile = [
      `Betreuungs-Check über Miteinander38 (Radio38-Kampagne).`,
      `Unterstützung für: ${antworten[FRAGE_FUER_WEN.key] ?? 'keine Angabe'}`,
      `Ort/PLZ: ${ort.trim() || 'keine Angabe'}`,
      `Zeitrahmen: ${antworten[FRAGE_ZEITRAHMEN.key] ?? 'keine Angabe'}`,
      `Pflegegrad: ${antworten[FRAGE_PFLEGEGRAD.key] ?? 'keine Angabe'}`,
      `Rückruf an: ${vorname.trim()} · ${telefon.trim()}`,
    ];
    if (notiz.trim()) teile.push('', `Mitteilung: ${notiz.trim()}`);
    return teile.join('\n');
  };

  const absenden = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Von Hand geprüft, weil das Formular `noValidate` trägt: Die Browsermeldung
    // („Füllen Sie dieses Feld aus") erscheint an einem Feld, das nach dem
    // Schrittwechsel womöglich gar nicht sichtbar ist, und sagt weniger als der
    // Satz hier. Pflicht sind nur Name und Rufnummer — ohne die können wir den
    // Rückruf nicht machen, um den es geht. Alles andere bleibt freiwillig.
    if (!vorname.trim()) {
      setError('Bitte sagen Sie uns Ihren Vornamen, damit wir Sie ansprechen können.');
      return;
    }
    if (!telefon.trim()) {
      setError('Bitte hinterlassen Sie eine Telefonnummer für den Rückruf.');
      return;
    }
    if (!einwilligung) {
      setError('Bitte stimmen Sie der Verarbeitung Ihrer Angaben zu.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      await submitContactRequest({
        firstName: vorname.trim(),
        email: email.trim() || undefined,
        phone: telefon.trim(),
        message: nachricht(),
        topic: 'CARE_24H',
        // Dass die Anfrage aus der Radiokampagne stammt, trägt der Auftritt
        // (siehe `publicApi.ts`). Hier steht nur noch, welches Formular es war —
        // der reine Pfad wäre auf dieser einseitigen Domain immer „/".
        sourcePath: '/betreuungs-check',
        details: beantwortete({ ...antworten, [FRAGE_ORT.key]: ort }),
        website: honeypot,
      });
      track(M38_EVENTS.checkGesendet);
      navigate('/danke');
    } catch (e: any) {
      setError(e.message);
      setSending(false);
    }
  };

  return (
    <div className="check-panel">
      <div className="progress" aria-hidden="true">
        <div style={{ width: `${((schritt + 1) / SCHRITTE) * 100}%` }} />
      </div>
      <p className="progress-label">
        Schritt {schritt + 1} von {SCHRITTE}
      </p>

      <form onSubmit={absenden} noValidate>
        {schritt === 0 && (
          <div
            onKeyDown={(e) => {
              perPfeiltaste.current = e.key.startsWith('Arrow');
            }}
            onPointerDown={() => {
              perPfeiltaste.current = false;
            }}
          >
            <h3 className="step-title" ref={ueberschrift} tabIndex={-1}>
              {FRAGE_FUER_WEN.label}
            </h3>
            <p className="note">Wählen Sie die passendste Antwort.</p>
            <Optionen
              frage={FRAGE_FUER_WEN}
              gewaehlt={antworten}
              onWaehle={waehleUndWeiter}
              ueberschrift={false}
              abwaehlbar={false}
            />
          </div>
        )}

        {schritt === 1 && (
          <div>
            <h3 className="step-title" ref={ueberschrift} tabIndex={-1}>
              Wie sieht der Bedarf aus?
            </h3>
            <p className="note">Eine grobe Einschätzung reicht völlig aus.</p>

            <label className="field-label" style={{ marginTop: 20 }}>
              {FRAGE_ORT.label}
              <input
                className="field"
                value={ort}
                onChange={(e) => setOrt(e.target.value)}
                autoComplete="postal-code"
                placeholder={FRAGE_ORT.platzhalter}
              />
              {FRAGE_ORT.hinweis && <span className="note">{FRAGE_ORT.hinweis}</span>}
            </label>

            <Optionen frage={FRAGE_ZEITRAHMEN} gewaehlt={antworten} onWaehle={waehle} />
            <Optionen frage={FRAGE_PFLEGEGRAD} gewaehlt={antworten} onWaehle={waehle} />
          </div>
        )}

        {schritt === 2 && (
          <div>
            <h3 className="step-title" ref={ueberschrift} tabIndex={-1}>
              Wie können wir Sie erreichen?
            </h3>
            <p className="note">Wir melden uns persönlich bei Ihnen.</p>

            <label className="field-label" style={{ marginTop: 20 }}>
              Vorname
              <input
                className="field"
                value={vorname}
                onChange={(e) => setVorname(e.target.value)}
                autoComplete="given-name"
                required
              />
            </label>
            <label className="field-label">
              Telefonnummer
              <input
                className="field"
                type="tel"
                value={telefon}
                onChange={(e) => setTelefon(e.target.value)}
                autoComplete="tel"
                placeholder="z. B. 0171 1234567"
                required
              />
            </label>
            <label className="field-label">
              <span>
                E-Mail <span className="note">(optional)</span>
              </span>
              <input
                className="field"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            <label className="field-label">
              <span>
                Möchten Sie uns noch etwas mitteilen? <span className="note">(optional)</span>
              </span>
              <textarea
                className="field"
                rows={3}
                value={notiz}
                onChange={(e) => setNotiz(e.target.value)}
                placeholder="Zum Beispiel, was Ihnen im Alltag gerade am meisten Sorgen macht."
              />
            </label>

            <HoneypotField value={honeypot} onChange={setHoneypot} />

            {/*
              Der Verweis auf die Datenschutzerklärung steht bewusst **außerhalb**
              des <label>: Läge er darin, schaltete ein Tipp darauf zusätzlich das
              Häkchen um — ein Klick, der zwei Dinge tut, von denen der Besucher
              nur eines wollte.
            */}
            <div className="privacy">
              <input
                id="m38-einwilligung"
                type="checkbox"
                checked={einwilligung}
                onChange={(e) => setEinwilligung(e.target.checked)}
              />
              <span>
                <label htmlFor="m38-einwilligung">
                  Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert und
                  verarbeitet werden.
                </label>{' '}
                Einzelheiten stehen in der <Link to="/datenschutz">Datenschutzerklärung</Link>.
              </span>
            </div>
          </div>
        )}

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <div className="form-nav">
          {schritt > 0 ? (
            <button type="button" className="btn btn--ghost" onClick={zurueck}>
              <Icon name="arrow-left" size={17} /> Zurück
            </button>
          ) : (
            <span />
          )}

          {schritt < SCHRITTE - 1 ? (
            <button type="button" className="btn btn--primary" onClick={weiter}>
              Weiter <Icon name="arrow-right" size={17} />
            </button>
          ) : (
            <button type="submit" className="btn btn--primary" disabled={sending}>
              {sending ? 'Wird gesendet …' : <T k="m38.check.absenden" />}
            </button>
          )}
        </div>
      </form>

      <p className="note" style={{ marginTop: 16, textAlign: 'center' }}>
        <T k="m38.check.fussnote" />
      </p>
    </div>
  );
};

/**
 * Antworten als Schaltflächen statt als Auswahlfeld.
 *
 * Auf dem Telefon kostet ein `<select>` drei Schritte — antippen, Liste öffnet,
 * wählen — für etwas, das ein Tipp erledigt, und versteckt bis dahin, was
 * überhaupt zur Wahl steht. Dieselbe Entscheidung wie im Fragebogen der
 * Kundenwebsite.
 *
 * Darunter liegen **echte Radios**, nur ungezeichnet (siehe `m38.css`). Damit
 * funktionieren Tastatur und Vorleseprogramme wie bei jedem Formular; eine
 * Sammlung von `<button aria-pressed>` sähe genauso aus, würde aber als eine
 * Reihe unabhängiger Umschalter vorgelesen statt als eine Frage mit Antworten.
 */
const Optionen: React.FC<{
  frage: FragebogenFrage;
  gewaehlt: Record<string, string>;
  onWaehle: (key: string, wert: string) => void;
  /** Frage über den Antworten zeigen. Aus, wo sie schon als Überschrift steht. */
  ueberschrift?: boolean;
  /**
   * Nochmal tippen hebt die Angabe auf. Aus bei Pflichtfragen: Dort bestätigt
   * der zweite Tipp die Antwort, statt sie zu löschen.
   */
  abwaehlbar?: boolean;
}> = ({ frage, gewaehlt, onWaehle, ueberschrift = true, abwaehlbar = true }) => {
  const labelId = `m38-frage-${frage.key}`;
  const optionen = frage.optionen ?? [];

  // Ob die Antworten nebeneinander passen, steht in den Daten und nicht in
  // einem Schalter hier: Wo `kurz` gepflegt ist, sind die Beschriftungen kurz
  // genug für Chips — das ist bei den Pflegegraden der Fall und sonst nirgends.
  const kompakt = optionen.some((option) => option.kurz);

  return (
    <div className="frage">
      {ueberschrift && <span id={labelId}>{frage.label}</span>}
      <div
        className={kompakt ? 'options options--compact' : 'options'}
        role="radiogroup"
        aria-label={ueberschrift ? undefined : frage.label}
        aria-labelledby={ueberschrift ? labelId : undefined}
      >
        {optionen.map((option) => {
          const ausgewaehlt = gewaehlt[frage.key] === option.wert;
          return (
            <label className="option" key={option.wert}>
              <input
                type="radio"
                name={frage.key}
                value={option.wert}
                checked={ausgewaehlt}
                onChange={() => onWaehle(frage.key, option.wert)}
                // Nochmal tippen hebt die Angabe auf. Ein Radio kennt von sich
                // aus keinen Weg zurück, und die Angaben hier sind freiwillig.
                // Ein Radio meldet den zweiten Tipp nicht als Änderung, deshalb
                // kommt auch die Bestätigung bei Pflichtfragen hier an.
                onClick={() => {
                  if (ausgewaehlt) onWaehle(frage.key, abwaehlbar ? '' : option.wert);
                }}
              />
              <span
                title={
                  ausgewaehlt && abwaehlbar
                    ? 'Nochmal tippen hebt die Angabe auf'
                    : option.kurz
                      ? option.wert
                      : undefined
                }
              >
                {option.kurz ?? option.wert}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default BetreuungsCheck;
